import { SubdirectoryArrowRight as SubdirectoryArrowRightIcon } from 'components/icons';
import PermissionButton from 'components/buttons/PermissionButton.tsx';
import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import ReadonlyField from 'components/forms/inputs/rhf-other/ReadonlyField';
import InfoTooltip from 'components/InfoTooltip.tsx';
import Loader from 'components/Loader';
import { useModal } from 'components/modals/useModal.ts';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import EditableInvoiceClientReference from 'pages/invoices/layout/views/components/EditableInvoiceClientReference.tsx';
import { InvoicePayersField, useInvoicePayersForm } from 'pages/invoices/payers/InvoicePayersField';
import { MANAGE_INVOICE_PAYERS_PERMISSION, useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { collectCorrections } from 'pages/invoices/util/invoiceUtil';
import EditableMatterClientReference from 'pages/matters/components/EditableMatterClientReference.tsx';
import { ReactNode, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts.ts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import {
    useCustomizeInvoiceMutation,
    useGetInvoiceAccountingTypesQuery,
    useGetInvoiceSpecTemplatesQuery,
    useGetInvoiceTemplatesQuery,
    useUpdateInvoiceBillingNotesMutation,
} from 'store/api/invoices';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features';
import { CustomizationDto, InvoiceData, InvoiceForm, InvoiceStatusType, PrepaymentDataListItem } from 'types/invoices';
import { getAddress, getOptionsFromClassifier } from 'utils/formUtil.ts';
import { t } from 'utils/getTranslations.ts';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { partyNameString } from 'utils/partyNameUtil';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useInvoice } from '../../../hooks/useInvoice.ts';
import CustomizeInvoiceModal from './CustomizeInvoiceModal.tsx';
import InvoiceBillingNotesModal from './InvoiceBillingNotesModal.tsx';
import { CUSTOMISE_SHEET } from 'components/modals/sideSheet';

type Props = {
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
    isInViewMode: boolean;
    submitInvoice: () => void;
    submitTemplateUpdate: () => void;
    isTemplateEditable: boolean;
    updateInvoiceLimited: () => void;
    existingPrepayments?: PrepaymentDataListItem[];
};

/** Where an invoice's billing notes are edited from: its payer's, a case client's, or a case's. */
type BillingNotesTarget = { billingNotes: string; partyId?: number; matterId?: number };

/** Opens the billing notes dialog for the payer, a case's client or a case; it saves on the invoice's owner. */
export function useOpenBillingNotesModal(invoiceId: number) {
    const { openModal, closeModal } = useModal();
    const [updateInvoiceBillingNotes] = useUpdateInvoiceBillingNotesMutation();
    return useCallback(
        ({ billingNotes, partyId, matterId }: BillingNotesTarget) =>
            openModal(
                <InvoiceBillingNotesModal
                    billingNotes={billingNotes}
                    partyId={partyId}
                    matterId={matterId}
                    onClose={closeModal}
                    onSubmit={updateInvoiceBillingNotes}
                    invoiceId={invoiceId}
                />
            ),
        [closeModal, invoiceId, openModal, updateInvoiceBillingNotes]
    );
}

/**
 * The Details card's fields, each with its gates and its save on change, for classic's card and for the new design's
 * Settings and Payer cards, which lay them out on their own grids. A card that saves on its Save passes callbacks that
 * do nothing. A card that saves the payer list on its Save saves the `payersForm` this returns.
 */
export function useDetailsCardFields({
    invoice,
    classifiers,
    isInViewMode,
    submitInvoice,
    submitTemplateUpdate,
    isTemplateEditable,
    updateInvoiceLimited,
    existingPrepayments,
}: Props) {
    const { control, setValue } = useFormContext<InvoiceForm>();
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const isExcludeFromQlikSenseEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_EXCLUDE_FROM_QLIK_SENSE);

    const { data: invoiceTemplates, isLoading: isLoadingInvoiceTemplates } = useGetInvoiceTemplatesQuery();
    const { data: invoiceSpecTemplates, isLoading: isLoadingInvoiceSpecTemplates } = useGetInvoiceSpecTemplatesQuery();
    const [customizeInvoice] = useCustomizeInvoiceMutation();
    const openBillingNotesModal = useOpenBillingNotesModal(invoice.id);
    const { hasCreditInvoice, originatesFromInvoice, correctionInvoice } = invoice;
    const creditInvoice = hasCreditInvoice && invoice.creditInvoices?.length ? invoice.creditInvoices[0] : undefined;
    const hasPermissionToUpdateAccountingType = useHasPermissions(['UPDATE.INVOICES.ACCOUNTING_TYPE']);
    const hasPermissionToUpdateReportExclusion = useHasPermissions(['UPDATE.INVOICES.REPORT_EXCLUSION.ALL']);
    const hasPermissionToReadReportExclusion = useHasPermissions(['READ.INVOICES.REPORT_EXCLUSION.ALL']);
    const hasUpdatePermission = useHasPermissions(['UPDATE.INVOICES.ALL']);
    const isSignedInvoice =
        invoice.invoiceStatusTypeCode === InvoiceStatusType.SENT ||
        invoice.invoiceStatusTypeCode === InvoiceStatusType.PAID;

    const { data: accountingTypes, isLoading: isLoadingAccountingTypes } = useGetInvoiceAccountingTypesQuery(
        invoice.id
    );
    const clientParty = useWatch({ control, name: 'clientParty' });
    const receiverParty = useWatch({ control, name: 'receiverParty' });
    const isSpecIncludedInInvoice = useWatch({ control, name: 'isSpecIncludedInInvoice' });
    const { isSent, isPaid, isCorrection } = useInvoice(invoice);
    const { hasPayerList, hasPaymentShare, isSharedPaymentCopy } = useSharedPayment(invoice);
    const canManagePayers = useHasPermissions([MANAGE_INVOICE_PAYERS_PERMISSION]);
    const payersForm = useInvoicePayersForm(invoice);
    const hasSeveralPayers = hasPayerList && payersForm.rows.length > 1;
    const payersReadOnlyNoteId = isInViewMode
        ? 'invoices.payers.read-only-not-editable'
        : !canManagePayers
          ? 'invoices.payers.read-only-info'
          : null;
    const isPrepaymentUsed = !!existingPrepayments?.some((prepayment) => prepayment.isConnected);
    const isPayerReadOnly = isInViewMode || isSent || isPaid || hasPaymentShare;
    const originatesFrom = isCorrection ? originatesFromInvoice : undefined;

    const accountingTypeOptions = useMemo(() => {
        const applicableAccountingTypes =
            accountingTypes?.flatMap((type) => type.values?.map((value) => value?.code)) || [];

        return (
            getOptionsFromClassifier(classifiers['INVOICE_ACCOUNTING_TYPE']) as {
                label: string;
                value: string;
            }[]
        ).filter((cl) => applicableAccountingTypes.includes(cl.value));
    }, [accountingTypes, classifiers]);

    const { isCreditFromDebit, isPostponed } = useInvoice(invoice);

    const { openModal, closeModal } = useModal();
    const isCustomizationEnabled = useIsFeatureEnabled(FeatureFlag.USE_CUSTOMIZED_INVOICE_TEMPLATE);
    const hasCustomizePermission = useHasPermissions([
        'READ.CUSTOMIZE_INVOICE_SPEC_TEMPLATE',
        'UPDATE.CUSTOMIZE_INVOICE_SPEC_TEMPLATE',
    ]);
    const isLoading = isLoadingInvoiceTemplates || isLoadingInvoiceSpecTemplates || isLoadingAccountingTypes;

    const baseCustomization = {
        ...invoice.invoiceTemplate?.supportedCustomizationOptionsDefaultValues,
        ...Object.fromEntries(
            Object.entries(invoice.invoiceSpecTemplate?.supportedCustomizationOptionsDefaultValues || {}).filter(
                ([, value]) => value !== null && value !== undefined
            )
        ),
    };

    const supportedCustomization: Partial<CustomizationDto> = {
        ...baseCustomization,
        ...Object.fromEntries(
            (Object.keys(baseCustomization) as (keyof CustomizationDto)[])
                .filter((key) => key in invoice.customization && invoice.customization[key] !== null)
                .map((key) => [key, invoice.customization[key]])
        ),
    };

    const openCustomize = () =>
        openModal(
            <CustomizeInvoiceModal
                invoiceId={invoice.id}
                customization={supportedCustomization}
                onClose={closeModal}
                customizeMutation={customizeInvoice}
                description={
                    <FormattedMessage
                        id="invoices.custom.sheet-description"
                        values={{
                            invoiceTemplate: invoice.invoiceTemplate?.name ?? '',
                            specTemplate: invoice.invoiceSpecTemplate?.name || 'none',
                        }}
                    />
                }
            />,
            undefined,
            { width: 'max-content', maxWidth: 'calc(100vw - 2rem)' },
            undefined,
            CUSTOMISE_SHEET
        );

    const openPayerBillingNotes = () =>
        openBillingNotesModal({
            billingNotes: invoice.clientParty.billingNotes ?? '',
            partyId: invoice.clientParty.id,
        });

    const payerBillingNotesButton = (
        <PermissionButton
            permissions={['UPDATE.INVOICES.BILLING_NOTES']}
            disabled={isPostponed}
            className="hover:bg-grey-100 hover:border-divider"
            variant="outlined"
            size="extraSmall"
            onClick={openPayerBillingNotes}
        >
            <FormattedMessage id="invoices.general-tab.details.edit-payer-billing-notes" />
        </PermissionButton>
    );
    const cannotChangePayerTooltip = isPrepaymentUsed ? (
        <InfoTooltip color={'secondary'} text={'invoices.tooltips.cannot-change-payer'} />
    ) : undefined;

    const linkFields = (
        <>
            {originatesFrom ? (
                <InputWrapper
                    name="originates-from"
                    label={'invoices.general-tab.details.originates-from'}
                    readonly={true}
                    hasErrors={false}
                >
                    <ReadonlyField
                        value={originatesFrom}
                        getLabel={() => originatesFrom.invoiceNumber}
                        getValue={() => originatesFrom.id}
                        linkTo="invoice"
                    />
                </InputWrapper>
            ) : null}
            {correctionInvoice ? (
                <InputWrapper
                    name="correction-invoice"
                    label={'invoices.general-tab.details.correction-invoices'}
                    readonly={true}
                    hasErrors={false}
                >
                    <div className="flex flex-col gap-2">
                        {collectCorrections(correctionInvoice).map((correction, index) => (
                            <div
                                key={correction.id}
                                className="flex items-center gap-1"
                                style={{ paddingLeft: index * 16 }}
                            >
                                {index > 0 && <SubdirectoryArrowRightIcon className="text-fg-secondary text-[16px]" />}
                                <ReadonlyField
                                    value={correction}
                                    getLabel={() => correction.invoiceNumber}
                                    getValue={() => correction.id}
                                    linkTo="invoice"
                                />
                            </div>
                        ))}
                    </div>
                </InputWrapper>
            ) : null}
            {invoice.isSplit ? (
                <InputWrapper
                    name="split-invoice"
                    label={
                        isSharedPaymentCopy
                            ? 'invoices.general-tab.details.shared-payment-with'
                            : 'invoices.general-tab.details.copy-of'
                    }
                    readonly={true}
                    hasErrors={false}
                >
                    {(invoice.invoiceCopies || [])
                        .filter((replica) => replica.id !== invoice.id)
                        .map((replica) => (
                            <div key={`${replica.id}`}>
                                {isSharedPaymentCopy ? (
                                    <RecordLink
                                        to={`/invoice/${replica.id}`}
                                        className="text-link text-sm no-underline hover:underline"
                                        label={replica.invoiceNumber}
                                    />
                                ) : (
                                    <ReadonlyField
                                        value={invoice}
                                        getLabel={() => replica.invoiceNumber}
                                        getValue={() => replica.id}
                                        linkTo="invoice"
                                    />
                                )}
                            </div>
                        ))}
                </InputWrapper>
            ) : null}
            {creditInvoice ? (
                <InputWrapper
                    name="credit-invoice"
                    label={'invoices.general-tab.details.credit-invoice'}
                    readonly={true}
                    hasErrors={false}
                >
                    <ReadonlyField
                        value={creditInvoice}
                        getLabel={() => creditInvoice.invoiceNumber}
                        getValue={() => creditInvoice.id}
                        linkTo="invoice"
                    />
                </InputWrapper>
            ) : null}
            {isCreditFromDebit ? (
                <InputWrapper
                    name="debit-invoice"
                    label={'invoices.general-tab.details.debit-invoice'}
                    readonly={true}
                    hasErrors={false}
                >
                    <ReadonlyField
                        value={invoice}
                        getLabel={() => invoice.debitInvoice?.invoiceNumber}
                        getValue={() => invoice.debitInvoice?.id}
                        linkTo="invoice"
                    />
                </InputWrapper>
            ) : null}
        </>
    );

    const officeField = (
        <DynamicSelectInput
            label="invoices.general-tab.details.issued-by-office"
            control={control}
            name="invoicedByOffice"
            paginated={{
                fetch: useLazyGetMinOfficesWithFiltersQuery,
                searchField: 'name',
                filters: 'isActive!=false',
            }}
            getOptionLabel="name"
            getOptionIdentifier="id"
            getOptionEndAdornment={officeInactiveSuffix}
            setValue={setValue}
            readonly={isInViewMode ? { linkTo: 'office' } : false}
            required
            onChange={submitInvoice}
        />
    );

    const invoiceTemplateField = (labelAction?: ReactNode) => (
        <SelectInput
            label="invoices.general-tab.details.invoice-template"
            control={control}
            name="invoiceTemplate"
            options={invoiceTemplates?.filter((it) => it.isCorrection === isCorrection) ?? []}
            getOptionIdentifier="id"
            getOptionLabel="name"
            setValue={setValue}
            readonly={!isTemplateEditable}
            required={true}
            noPreSelect
            onChange={submitTemplateUpdate}
            rightLabelComponent={labelAction}
        />
    );

    /*
     * The two CUSTOMIZE_INVOICE_SPEC_TEMPLATE privileges open this button, and
     * PermissionButton matches ANY listed one: the UPDATE holder edits, the READ holder
     * gets the same modal read-only (CustomizeInvoiceModal disables every checkbox and
     * the footer off its own UPDATE check). isTemplateEditable must not gate it either
     * way — customising the spec template is a presentation choice, not an invoice edit,
     * and that flag is false on every externally approved invoice, which the backend
     * rule still accepts.
     */
    const customizeButton = (
        <FeatureFlagged action="show" flag={FeatureFlag.USE_CUSTOMIZED_INVOICE_TEMPLATE}>
            <PermissionButton
                className="hover:bg-grey-100 hover:border-divider mt-2"
                size="extraSmall"
                variant="outlined"
                permissions={['READ.CUSTOMIZE_INVOICE_SPEC_TEMPLATE', 'UPDATE.CUSTOMIZE_INVOICE_SPEC_TEMPLATE']}
                onClick={openCustomize}
            >
                <FormattedMessage id="invoices.custom.customize" />
            </PermissionButton>
        </FeatureFlagged>
    );

    const supervisedByField = (
        <SelectInput
            label="invoices.general-tab.details.supervised-by"
            control={control}
            setValue={setValue}
            name="supervisedByEmployee"
            options={[invoice.supervisedByEmployee]}
            getOptionLabel="name"
            getOptionIdentifier="id"
            readonly={{ linkTo: 'employee' }}
            disabled
            gap={2}
            onChange={submitInvoice}
        />
    );

    const specTemplateField = (
        <SelectInput
            label="invoices.general-tab.details.invoice-spec-template"
            control={control}
            name="invoiceSpecTemplate"
            options={invoiceSpecTemplates ?? []}
            getOptionIdentifier="id"
            getOptionLabel="name"
            setValue={setValue}
            readonly={!isTemplateEditable}
            required={true}
            noPreSelect
            onChange={submitTemplateUpdate}
        />
    );

    const includeSpecField = (
        <CheckboxInput
            name="isSpecIncludedInInvoice"
            control={control}
            label={'invoices.general-tab.details.merge-invoice-and-spec'}
            readonly={isInViewMode}
            onChange={(checked) => {
                if (checked) {
                    setValue('isSpecExcludedFromEmail', false, { shouldDirty: true });
                }
                submitInvoice();
            }}
        />
    );

    const excludeSpecField = (
        <CheckboxInput
            name="isSpecExcludedFromEmail"
            control={control}
            label={'invoices.general-tab.details.exclude-spec-pdf-attachment-in-email'}
            readonly={isInViewMode}
            disabled={isSpecIncludedInInvoice}
            onChange={submitInvoice}
        />
    );

    const signedByField = (
        <DynamicSelectInput
            label="invoices.general-tab.details.signed-by"
            control={control}
            setValue={setValue}
            name="signedByEmployee"
            paginated={{
                fetch: useLazyGetEmployeesListWithFiltersQuery,
                searchField: 'fullName',
            }}
            getOptionLabel="name"
            getOptionIdentifier="id"
            readonly={isInViewMode ? { linkTo: 'employee' } : false}
            required
            disabled={isSignedInvoice}
            tooltip={<InfoTooltip text={'invoices.general-tab.details.signed-by-info'} />}
            onChange={submitInvoice}
        />
    );

    const accountingTypeField = (
        <FeatureFlagged action="show" flag={FeatureFlag.EDIT_INVOICE_ACCOUNTING_TYPE}>
            <SelectInput
                control={control}
                name="invoiceAccountingType"
                label="invoices.general-tab.details.invoice-accounting-type"
                options={accountingTypeOptions}
                getOptionIdentifier="value"
                getOptionLabel="label"
                setValue={setValue}
                readonly={isInViewMode}
                required
                disabled={!hasPermissionToUpdateAccountingType}
                onChange={submitInvoice}
            />
        </FeatureFlagged>
    );

    const vatCalculationField = (
        <FeatureFlagged action="show" flag={FeatureFlag.EDIT_INVOICE_VAT_CALCULATION_TYPE}>
            <SelectInput
                control={control}
                name="vatCalculationType"
                label="invoices.general-tab.details.vat-calculation-type"
                options={
                    (getOptionsFromClassifier(classifiers['VAT_CALCULATION_TYPE']) as {
                        label: string;
                        value: string;
                    }[]) ?? []
                }
                getOptionIdentifier="value"
                getOptionLabel="label"
                setValue={setValue}
                readonly={isInViewMode}
                required
                onChange={submitInvoice}
            />
        </FeatureFlagged>
    );

    const canAddPayers = !isPayerReadOnly && canManagePayers && !isPrepaymentUsed;
    const payerField = ({ labelAction, inCard }: { labelAction?: ReactNode; inCard?: boolean }) =>
        hasPayerList ? (
            <InvoicePayersField
                invoice={invoice}
                payersForm={payersForm}
                readOnlyNoteId={payersReadOnlyNoteId}
                isSinglePayerReadOnly={isPayerReadOnly}
                canAddPayers={canAddPayers}
                labelAction={labelAction}
                isSinglePayerDisabled={isPrepaymentUsed}
                singlePayerTooltip={cannotChangePayerTooltip}
                inCard={inCard}
            />
        ) : (
            <ContactDynamicSelectPicker
                control={control}
                name="clientParty"
                label="invoices.general-tab.details.payer"
                setValue={setValue}
                getOptionLabel={(option) =>
                    partyNameString(option, {
                        showPartyNumber: true,
                        isPartyNumberUsageEnabled: isPartyNumberUsageEnabled,
                    })
                }
                paginated={{
                    fetch: useLazyGetMinContactsListWithFiltersQuery,
                    searchField: ['name', 'partyNumber'],
                }}
                PickerModalComponent={ContactPickerModal}
                required
                onChange={submitInvoice}
                disabled={isPrepaymentUsed}
                rightLabelComponent={labelAction}
                tooltip={cannotChangePayerTooltip}
                readonly={isPayerReadOnly ? { linkTo: 'contact' } : undefined}
            />
        );

    const clientRefField = (
        <InputWrapper
            name="client-ref"
            label={'invoices.general-tab.details.client-ref'}
            readonly={true}
            hasErrors={false}
        >
            <EditableInvoiceClientReference
                invoiceId={invoice.id}
                clientReference={invoice.clientReference}
                isEditEnabled={invoice.isEditable}
            />
        </InputWrapper>
    );

    const receiverPicker = (
        <ContactDynamicSelectPicker
            control={control}
            name="receiverParty"
            label="invoices.general-tab.details.receiver"
            setValue={setValue}
            getOptionLabel={(option) =>
                partyNameString(option, {
                    showPartyNumber: true,
                    isPartyNumberUsageEnabled: isPartyNumberUsageEnabled,
                })
            }
            paginated={{
                fetch: useLazyGetMinContactsListWithFiltersQuery,
                searchField: ['name', 'partyNumber'],
            }}
            PickerModalComponent={ContactPickerModal}
            clearable
            onChange={submitInvoice}
            readonly={isInViewMode || isSent || isPaid ? { linkTo: 'contact' } : undefined}
        />
    );

    const specificInstructionsField = (
        <TextAreaInput
            control={control}
            name="billingInstructions"
            label="invoices.general-tab.details.specific-instructions"
            readonly={(isInViewMode && !hasUpdatePermission) || isPostponed}
            onBlur={updateInvoiceLimited}
            enableAutofill
        />
    );

    const paymentNotificationField = (
        <DynamicMultiSelectInput
            setValue={setValue}
            control={control}
            label="contacts.billing.invoice-payment-notification-to"
            name="paymentNotificationReceivers"
            paginated={{
                fetch: useLazyGetEmployeesListWithFiltersQuery,
                searchField: 'fullName',
            }}
            getOptionLabel="name"
            getOptionIdentifier="id"
            placeholder="contacts.billing.invoice-payment-notification-to"
            readonly={isInViewMode}
            onChange={submitInvoice}
        />
    );

    const eBillingUrlField = clientParty ? (
        <InputWrapper
            name="clientParty"
            label={'invoices.general-tab.details.payer-e-billing-system-url'}
            readonly={true}
            hasErrors={false}
            className="mr-8"
        >
            <>
                {clientParty?.externalBillingInfo ? (
                    <a
                        href={clientParty.externalBillingInfo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-link w-fit no-underline hover:underline"
                    >
                        {clientParty.externalBillingInfo}
                    </a>
                ) : (
                    '-'
                )}
            </>
        </InputWrapper>
    ) : null;

    const showsExcludeFromQlikSense =
        isExcludeFromQlikSenseEnabled && (hasPermissionToReadReportExclusion || hasPermissionToUpdateReportExclusion);
    const excludeFromQlikSenseField = showsExcludeFromQlikSense ? (
        <CheckboxInput
            name="excludeFromReporting"
            control={control}
            label={'invoices.general-tab.invoice-details.exclude-from-report'}
            readonly={(isInViewMode && !hasUpdatePermission) || isPostponed}
            disabled={!hasPermissionToUpdateReportExclusion}
            onChange={updateInvoiceLimited}
        />
    ) : null;

    return {
        isLoading,
        clientParty,
        receiverParty,
        isSpecIncludedInInvoice,
        isPostponed,
        hasPayerList,
        hasSeveralPayers,
        payersForm,
        canAddPayers,
        payersReadOnlyNoteId,
        // As each field's own lock has it: the list by the right to manage payers, a single payer by the invoice's.
        isPayerEditable: hasSeveralPayers ? payersReadOnlyNoteId === null : !isPayerReadOnly,
        isReceiverEditable: !(isInViewMode || isSent || isPaid),
        canCustomize: isCustomizationEnabled && hasCustomizePermission,
        openCustomize,
        openPayerBillingNotes,
        openBillingNotesModal,
        payerBillingNotesButton,
        linkFields,
        officeField,
        invoiceTemplateField,
        customizeButton,
        supervisedByField,
        specTemplateField,
        includeSpecField,
        excludeSpecField,
        signedByField,
        accountingTypeField,
        vatCalculationField,
        payerField,
        clientRefField,
        receiverPicker,
        specificInstructionsField,
        paymentNotificationField,
        eBillingUrlField,
        showsExcludeFromQlikSense,
        excludeFromQlikSenseField,
    };
}

const DetailsCard = (props: Props) => {
    const { invoice } = props;
    const fields = useDetailsCardFields(props);
    const { clientParty, receiverParty, hasSeveralPayers, isPostponed, openBillingNotesModal } = fields;

    if (fields.isLoading) {
        return <Loader />;
    }

    return (
        <div className="flex flex-col gap-4">
            {fields.linkFields}
            <div className="flex flex-col gap-4 lg:flex-row">
                {fields.officeField}
                <div className="inline-block w-full">
                    {fields.invoiceTemplateField()}
                    {fields.customizeButton}
                </div>
            </div>
            <div className="flex flex-col gap-4 lg:flex-row">
                {fields.supervisedByField}
                <div className="inline-block w-full">
                    {fields.specTemplateField}
                    {fields.includeSpecField}
                    {fields.excludeSpecField}
                </div>
            </div>
            <div className="flex flex-col gap-4 lg:flex-row">
                {fields.signedByField}
                {fields.accountingTypeField}
            </div>
            {fields.vatCalculationField}
            {fields.payerField({ labelAction: fields.payerBillingNotesButton })}
            {fields.clientRefField}
            {clientParty ? (
                <div className="flex flex-col flex-wrap lg:flex-row">
                    {hasSeveralPayers ? (
                        <p className="text-fg-secondary mb-2 w-full text-sm">
                            <FormattedMessage
                                id="invoices.payers.main-payer-details"
                                values={{ name: <span className="text-fg font-medium">{clientParty.name}</span> }}
                            />
                        </p>
                    ) : null}
                    <InputWrapper
                        name="official-name"
                        label={'official-name'}
                        readonly={true}
                        hasErrors={false}
                        noFullWidthInputWrapper
                        className="mr-8"
                    >
                        <>
                            {clientParty.officialName ? (
                                <PartyName party={clientParty} isLink useOfficialName variant="readonly-other" />
                            ) : (
                                '-'
                            )}
                        </>
                    </InputWrapper>
                    <InputWrapper
                        name="is-vat-liable"
                        label={'is-vat-liable'}
                        readonly={true}
                        hasErrors={false}
                        noFullWidthInputWrapper
                        className="mr-8"
                    >
                        <>{clientParty.isVatLiable ? t('yes') : t('no')}</>
                    </InputWrapper>
                    {clientParty.isVatLiable ? (
                        <InputWrapper
                            name="vat-number"
                            label={'vat-number'}
                            readonly={true}
                            hasErrors={false}
                            noFullWidthInputWrapper
                            className="mr-8"
                        >
                            <>{clientParty.vatRegNum ?? '-'}</>
                        </InputWrapper>
                    ) : null}
                    <InputWrapper
                        name="email"
                        label={'email'}
                        readonly={true}
                        hasErrors={false}
                        noFullWidthInputWrapper
                        className="mr-8"
                    >
                        <>{clientParty.mainEmail ?? '-'}</>
                    </InputWrapper>
                    <InputWrapper
                        name="billing-address"
                        label={
                            clientParty.billingAddress
                                ? 'billing-address'
                                : clientParty.registrationAddress
                                  ? 'registration-address'
                                  : 'billing-address'
                        }
                        readonly={true}
                        hasErrors={false}
                        noFullWidthInputWrapper
                    >
                        <>
                            {clientParty.billingAddress
                                ? getAddress(clientParty.billingAddress, clientParty.billingAddress.countryCode)
                                : clientParty.registrationAddress
                                  ? getAddress(
                                        clientParty.registrationAddress,
                                        clientParty.registrationAddress.countryCode
                                    )
                                  : '-'}
                        </>
                    </InputWrapper>
                </div>
            ) : null}
            <FeatureFlagged action="show" flag={FeatureFlag.INVOICE_SERVICE_RECIPIENT_FIELD}>
                <>
                    {fields.receiverPicker}
                    {receiverParty ? (
                        <div className="flex flex-col flex-wrap lg:flex-row">
                            <InputWrapper
                                name="receiver-official-name"
                                label={'official-name'}
                                readonly={true}
                                hasErrors={false}
                                noFullWidthInputWrapper
                                className="mr-8"
                            >
                                <>
                                    {receiverParty.officialName ? (
                                        <PartyName
                                            party={receiverParty}
                                            isLink
                                            useOfficialName
                                            variant="readonly-other"
                                        />
                                    ) : (
                                        '-'
                                    )}
                                </>
                            </InputWrapper>
                            <InputWrapper
                                name="receiver-is-vat-liable"
                                label={'is-vat-liable'}
                                readonly={true}
                                hasErrors={false}
                                noFullWidthInputWrapper
                                className="mr-8"
                            >
                                <>{receiverParty.isVatLiable ? t('yes') : t('no')}</>
                            </InputWrapper>
                            {receiverParty.isVatLiable ? (
                                <InputWrapper
                                    name="receiver-vat-number"
                                    label={'vat-number'}
                                    readonly={true}
                                    hasErrors={false}
                                    noFullWidthInputWrapper
                                    className="mr-8"
                                >
                                    <>{receiverParty.vatRegNum ?? '-'}</>
                                </InputWrapper>
                            ) : null}
                            <InputWrapper
                                name="receiver-email"
                                label={'email'}
                                readonly={true}
                                hasErrors={false}
                                noFullWidthInputWrapper
                                className="mr-8"
                            >
                                <>{receiverParty.mainEmail ?? '-'}</>
                            </InputWrapper>
                            <InputWrapper
                                name="receiver-billing-address"
                                label={
                                    receiverParty.billingAddress
                                        ? 'billing-address'
                                        : receiverParty.registrationAddress
                                          ? 'registration-address'
                                          : 'billing-address'
                                }
                                readonly={true}
                                hasErrors={false}
                                noFullWidthInputWrapper
                            >
                                <>
                                    {receiverParty.billingAddress
                                        ? getAddress(
                                              receiverParty.billingAddress,
                                              receiverParty.billingAddress.countryCode
                                          )
                                        : receiverParty.registrationAddress
                                          ? getAddress(
                                                receiverParty.registrationAddress,
                                                receiverParty.registrationAddress.countryCode
                                            )
                                          : '-'}
                                </>
                            </InputWrapper>
                        </div>
                    ) : null}
                </>
            </FeatureFlagged>
            <InputWrapper
                name="matter"
                label={'invoices.general-tab.details.issued-for-cases'}
                readonly={true}
                hasErrors={false}
            >
                <div className="flex flex-col gap-y-2">
                    {(invoice.invoiceMatters || [])
                        .toSorted((a, b) =>
                            (a.matterNumber ?? '').localeCompare(b.matterNumber ?? '', undefined, { numeric: true })
                        )
                        .map((invoiceMatter, index) => (
                            <div key={`${invoiceMatter.matterId || index}`} className="flex flex-wrap justify-between">
                                <ReadonlyField
                                    value={invoiceMatter}
                                    getLabel={() => invoiceMatter.fullName}
                                    getValue={() => invoiceMatter.matterId}
                                    getSuffix={() => (
                                        <div className="flex flex-row">
                                            <span>(</span>
                                            <PartyName
                                                party={invoiceMatter.clientParty}
                                                isLink
                                                variant="readonly-other"
                                                showPartyNumber
                                            />
                                            <span>)</span>
                                            <div className="ml-1 flex flex-row items-center gap-1">
                                                <span>
                                                    | <b>{t('invoices.general-tab.details.matter-ref')}:</b>
                                                </span>
                                                <EditableMatterClientReference
                                                    matterId={invoiceMatter.matterId}
                                                    matterClientReference={invoiceMatter.matterClientReference}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    linkTo="matter"
                                />
                                <div className="flex flex-row gap-2">
                                    {invoiceMatter.clientParty.id !== invoice.clientParty.id ? (
                                        <PermissionButton
                                            permissions={['UPDATE.INVOICES.BILLING_NOTES']}
                                            disabled={isPostponed}
                                            className="hover:bg-grey-100 hover:border-divider"
                                            variant="outlined"
                                            size="extraSmall"
                                            onClick={() =>
                                                openBillingNotesModal({
                                                    billingNotes: invoiceMatter.clientParty.billingNotes ?? '',
                                                    partyId: invoiceMatter.clientParty.id,
                                                })
                                            }
                                        >
                                            <FormattedMessage id="invoices.general-tab.details.edit-client-billing-notes" />
                                        </PermissionButton>
                                    ) : null}
                                    <PermissionButton
                                        permissions={['UPDATE.INVOICES.BILLING_NOTES']}
                                        disabled={isPostponed}
                                        className="hover:bg-grey-100 hover:border-divider"
                                        variant="outlined"
                                        size="extraSmall"
                                        onClick={() =>
                                            openBillingNotesModal({
                                                billingNotes: invoiceMatter.matterBillingDetails.billingNotes ?? '',
                                                matterId: invoiceMatter.matterId,
                                            })
                                        }
                                    >
                                        <FormattedMessage id="invoices.general-tab.details.edit-case-billing-notes" />
                                    </PermissionButton>
                                </div>
                            </div>
                        ))}
                </div>
            </InputWrapper>
            {fields.specificInstructionsField}
            {fields.paymentNotificationField}
            {fields.eBillingUrlField}
            {fields.excludeFromQlikSenseField}
        </div>
    );
};

export default DetailsCard;
